// src/pages/insurance/InsuranceFormPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import DocumentManager from "../../components/documents/DocumentManager";

import {
  createInsurance,
  updateInsurance,
  getInsuranceParentDetails,
} from "../../api/insuranceApi";
import { getPropertyById } from "../../api/unitApi";

import {
  Box,
  Paper,
  TextField,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";

function InsuranceFormPage() {
  const { propertyId, unitId, insuranceId } = useParams();
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const isEdit = Boolean(insuranceId);
  const isUnitContext = Boolean(unitId);
  const parentId = isUnitContext ? unitId : propertyId;

  // Determine the back path based on context
  const backPath = isUnitContext
    ? `/units/${unitId}/insurance`
    : `/properties/${propertyId}/insurance`;
  const goBack = useBackNavigation(backPath);

  const INSURANCE_TYPES = ["Estates", "Block", "D&O", "Engineering", "Other"];

  const [form, setForm] = useState({
    policy_number: "",
    provider_name: "",
    insurance_type: "",
    coverage_type: "",
    start_date: "",
    renewal_date: "",
    premium_amount: "",
  });
  const [resolvedPropertyId, setResolvedPropertyId] = useState(propertyId || "");

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ------------------------------
  // Load parent details for breadcrumbs
  // ------------------------------
  useEffect(() => {
    const loadBreadcrumbs = async () => {
      try {
        const data = await getInsuranceParentDetails(propertyId, unitId);
        const actionLabel = isEdit ? "Edit" : "Create";

        if (isUnitContext) {
          const propertyData = await getPropertyById(data.unit.property_id);
          setResolvedPropertyId(data.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${data.unit.property_id}/details` },
            {
              label: `Unit ${data.unit.unit_name || data.unit.unit_code || data.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Insurance", path: `/units/${unitId}/insurance` },
            { label: actionLabel, path: null },
          ]);
        } else {
          setResolvedPropertyId(propertyId);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: data.name, path: `/properties/${propertyId}/details` },
            { label: "Insurance", path: `/properties/${propertyId}/insurance` },
            { label: actionLabel, path: null },
          ]);
        }
      } catch (err) {
        console.error("Failed to load breadcrumbs:", err);
      }
    };

    loadBreadcrumbs();
    return () => clearCustomPath();
  }, [propertyId, unitId, isUnitContext, isEdit, setCustomPath, clearCustomPath]);

  // ------------------------------
  // Load existing policy if editing
  // ------------------------------
  useEffect(() => {
    if (!isEdit) return;

    const loadPolicy = async () => {
      try {
        const data = await getInsuranceParentDetails(propertyId, unitId);
        const policy = data.insurance_policies?.find(
          (p) => p.id === parseInt(insuranceId)
        );

        if (policy) {
          setForm({
            policy_number: policy.policy_number || "",
            provider_name: policy.provider_name || "",
            insurance_type: policy.insurance_type || "",
            coverage_type: policy.coverage_type || "",
            start_date: policy.start_date || "",
            renewal_date: policy.renewal_date || "",
            premium_amount: policy.premium_amount || "",
          });
        }
      } catch (err) {
        setError("Failed to load policy details.");
      }
    };

    loadPolicy();
  }, [isEdit, insuranceId, propertyId, unitId]);

  // ------------------------------
  // Form handlers
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (isEdit) return;
    if (!form.start_date || form.renewal_date) return;
    const start = new Date(form.start_date);
    if (Number.isNaN(start.getTime())) return;
    start.setFullYear(start.getFullYear() + 1);
    const nextRenewal = start.toISOString().split("T")[0];
    setForm((prev) => ({ ...prev, renewal_date: nextRenewal }));
  }, [form.start_date, form.renewal_date, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    const payload = {
      ...form,
      ...(isUnitContext ? { unit_id: parentId } : { property_id: parentId }),
    };

    try {
      if (isEdit) {
        await updateInsurance(insuranceId, payload);
      } else {
        await createInsurance(payload);
      }

      navigate(
        isUnitContext
          ? `/units/${unitId}/insurance`
          : `/properties/${propertyId}/insurance`,
        {
          state: {
            toast: {
              message: isEdit ? "Insurance policy updated" : "Insurance policy created",
              severity: "success",
            },
          },
        }
      );
    } catch {
      setToast({
        open: true,
        message: "Failed to save policy. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------
  // UI
  // ------------------------------
  return (
    <Layout>
      <Box className="page-container">
        <Paper sx={{ p: 4, width: "100%" }} elevation={2}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            {isEdit ? "Edit Insurance Policy" : "Add Insurance Policy"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {/* Row 1 — policy / provider / insurance / coverage only (dates never wrap here) */}
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Policy Number"
                    name="policy_number"
                    fullWidth
                    required
                    value={form.policy_number}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Provider Name"
                    name="provider_name"
                    fullWidth
                    required
                    value={form.provider_name}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: { sm: 280 } }}>
                    <InputLabel id="insurance-type-label">Insurance Type</InputLabel>
                    <Select
                      labelId="insurance-type-label"
                      label="Insurance Type"
                      name="insurance_type"
                      value={form.insurance_type}
                      onChange={handleChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      {INSURANCE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Coverage Type"
                    name="coverage_type"
                    fullWidth
                    value={form.coverage_type}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              {/* Row 2 — start (leading), renewal, premium, actions */}
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={6} sm={6} md={3}>
                  <TextField
                    label="Start Date"
                    name="start_date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.start_date}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6} sm={6} md={3}>
                  <TextField
                    label="Renewal Date"
                    name="renewal_date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.renewal_date}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Premium Amount (£)"
                    name="premium_amount"
                    type="number"
                    fullWidth
                    required
                    value={form.premium_amount}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 1.5,
                    alignSelf: "flex-end",
                    pb: { xs: 0, md: "2px" },
                  }}
                >
                  <Button type="submit" variant="contained" disabled={saving}>
                    {isEdit ? "Update Policy" : "Create Policy"}
                  </Button>
                  <Button variant="outlined" onClick={() => goBack()}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Paper>

        <DocumentManager
          propertyId={resolvedPropertyId}
          targetType="insurance"
          targetId={insuranceId}
        />
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default InsuranceFormPage;
